import { getCO2Emission } from '@common/functions';
import { makeStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import React, { FC, useEffect, useMemo } from 'react';

import { DateFormatter, NullableFormatter, NumberFormatter } from '~/@components/Formatters';
import { LocalizableText } from '~/@components/LocalizableText';
import { ISochiTableColumn, SochiTable } from '~/@sochi-components/SochiTable';
import { SochiHeadCellAlignment } from '~/@sochi-components/SochiTable/SochiTableHeadCell';
import { ProjectTimeSpentFilterField, projectTimeSpentStore } from '~/@user-store/timeSpent/timeSpentStore';
import { showProjectTimeSpentDialog } from '~/@views/UserView/ProjectPage/ProjectTimeSpent/ProjectTimeSpentEditForm/ProjectTimeSpentDialog';
import { ProjectTimeSpentOrderDateCell } from '~/@views/UserView/ProjectPage/ProjectTimeSpent/ProjectTimeSpentEditForm/ProjectTimeSpentOrderDateCell';
import { TimeSpentTableHeadCell } from '~/@views/UserView/ProjectPage/ProjectTimeSpent/TimeSpentTableHeadCell';
import { Project, ProjectTimeSpentRecord } from '~/@views/UserView/ProjectPage/ProjectTimeSpent/types';
import { commonPrecisions } from '~/config/enum';
import { ProjectQuery_project_dumpLoads_timeSpent } from '~/graphql';

type Props = {
    project: Project;
};

const mapTimeSpentToRecord = (input: ProjectQuery_project_dumpLoads_timeSpent): ProjectTimeSpentRecord => ({
    ...input,
    userName: input.user?.name,
    vehicleTypeName: input.vehicleType?.name,
    co2: input.vehicleType ? getCO2Emission(input.vehicleType, input.fuel, input.hoursSpent, 'hour') : null,
    userId: input.user?.id || '',
});

export const ProjectTimeSpentTable: FC<Props> = observer(({ project }) => {
    const { leftAlignCell, centerAlignCell, commentCell } = useStyles();

    useEffect(() => {
        projectTimeSpentStore.setList(project.dumpLoads.flatMap(d => d.timeSpent.map(mapTimeSpentToRecord)));
    }, [project]);

    useEffect(() => {
        return () => projectTimeSpentStore.clear();
    }, []);

    const columns = useMemo(
        (): Array<ISochiTableColumn<ProjectTimeSpentRecord>> => [
            {
                render: data => (
                    <ProjectTimeSpentOrderDateCell
                        timeSpent={data}
                        project={project}
                        onEdit={() => showProjectTimeSpentDialog(project, data)}
                    />
                ),
                title: (
                    <TimeSpentTableHeadCell
                        fieldName={ProjectTimeSpentFilterField.date}
                        title={<LocalizableText code={'ProjectDocuments.timeSpent.date'} />}
                        withFilter
                        withSort
                        alignment={SochiHeadCellAlignment.LEFT}
                    />
                ),
                cellClassName: leftAlignCell,
            },
            {
                render: data => <DateFormatter>{data.endDate}</DateFormatter>,
                title: (
                    <TimeSpentTableHeadCell
                        fieldName={ProjectTimeSpentFilterField.endDate}
                        title={<LocalizableText code={'ProjectDocuments.timeSpent.endDate'} />}
                        withFilter
                        withSort
                        alignment={SochiHeadCellAlignment.LEFT}
                    />
                ),
                cellClassName: leftAlignCell,
            },
            {
                render: data => data.dumpLoadNumber,
                title: (
                    <TimeSpentTableHeadCell
                        fieldName={ProjectTimeSpentFilterField.dumpLoadNumber}
                        title={<LocalizableText code={'ProjectDocuments.timeSpent.dumpLoadNumber'} />}
                        withFilter
                        withSort
                        alignment={SochiHeadCellAlignment.LEFT}
                    />
                ),
                cellClassName: leftAlignCell,
            },
            {
                render: data => <NullableFormatter>{data.userName}</NullableFormatter>,
                title: (
                    <TimeSpentTableHeadCell
                        fieldName={ProjectTimeSpentFilterField.userName}
                        title={<LocalizableText code={'ProjectDocuments.timeSpent.user'} />}
                        withFilter
                        withSort
                        alignment={SochiHeadCellAlignment.LEFT}
                    />
                ),
                cellClassName: leftAlignCell,
            },
            {
                render: data => data.licensePlate,
                title: (
                    <TimeSpentTableHeadCell
                        fieldName={ProjectTimeSpentFilterField.licensePlate}
                        title={<LocalizableText code={'ProjectDocuments.timeSpent.licensePlate'} />}
                        withFilter
                        withSort
                        alignment={SochiHeadCellAlignment.LEFT}
                    />
                ),
                cellClassName: leftAlignCell,
            },
            {
                render: data => <NullableFormatter>{data.vehicleTypeName}</NullableFormatter>,
                title: (
                    <TimeSpentTableHeadCell
                        fieldName={ProjectTimeSpentFilterField.vehicleTypeName}
                        title={<LocalizableText code={'ProjectDocuments.timeSpent.vehicleType'} />}
                        withFilter
                        withSort
                        alignment={SochiHeadCellAlignment.LEFT}
                    />
                ),
                cellClassName: leftAlignCell,
            },
            {
                render: data => data.fuel,
                title: (
                    <TimeSpentTableHeadCell
                        fieldName={ProjectTimeSpentFilterField.fuel}
                        title={<LocalizableText code={'ProjectDocuments.timeSpent.fuel'} />}
                        withFilter
                        withSort
                        alignment={SochiHeadCellAlignment.LEFT}
                    />
                ),
                cellClassName: centerAlignCell,
            },
            {
                render: data => data.engine,
                title: (
                    <TimeSpentTableHeadCell
                        fieldName={ProjectTimeSpentFilterField.engine}
                        title={<LocalizableText code={'ProjectDocuments.timeSpent.engine'} />}
                        withFilter
                        withSort
                        alignment={SochiHeadCellAlignment.LEFT}
                    />
                ),
                cellClassName: centerAlignCell,
            },
            {
                render: data => (
                    <NumberFormatter precision={commonPrecisions.amount}>{data.hoursSpent}</NumberFormatter>
                ),
                title: (
                    <TimeSpentTableHeadCell
                        fieldName={ProjectTimeSpentFilterField.hoursSpent}
                        title={<LocalizableText code={'ProjectDocuments.timeSpent.hoursSpent'} />}
                        withFilter
                        withSort
                        alignment={SochiHeadCellAlignment.LEFT}
                    />
                ),
            },
            {
                render: data => <NumberFormatter precision={commonPrecisions.decimal}>{data.co2}</NumberFormatter>,
                title: (
                    <TimeSpentTableHeadCell
                        fieldName={ProjectTimeSpentFilterField.co2}
                        title={<LocalizableText code={'ProjectDocuments.timeSpent.co2'} />}
                        withFilter
                        withSort
                        alignment={SochiHeadCellAlignment.LEFT}
                    />
                ),
            },
            {
                render: data => <NullableFormatter>{data.comment}</NullableFormatter>,
                title: (
                    <TimeSpentTableHeadCell
                        fieldName={ProjectTimeSpentFilterField.comment}
                        title={<LocalizableText code={'ProjectDocuments.timeSpent.comment'} />}
                        withFilter
                        withSort
                        alignment={SochiHeadCellAlignment.LEFT}
                    />
                ),
                cellClassName: commentCell,
            },
        ],
        [centerAlignCell, leftAlignCell, commentCell, project]
    );

    return (
        <SochiTable
            columns={columns}
            items={projectTimeSpentStore.filteredList}
            keyGetter={item => item.id}
            emptyListMessage={
                <LocalizableText
                    code={
                        projectTimeSpentStore.list.length > 0
                            ? 'noDataForFilter'
                            : 'ProjectDocuments.timeSpent.noTimeSpent'
                    }
                />
            }
            stickyFirstColumn
        />
    );
});

const useStyles = makeStyles(() => ({
    leftAlignCell: {
        textAlign: 'left',
    },
    centerAlignCell: {
        textAlign: 'center',
    },
    commentCell: {
        textAlign: 'left',
        maxWidth: '10vw',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
}));
